import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { Modal } from '../../components/Modal';
import { useErrorNotification } from '../../hooks/useErrorNotification';
import {
  type StatusMessage,
  type SubscriptionPlan,
  type StripeCatalogResponse,
} from './types';

type SubscriptionPlanFormValues = {
  code: string;
  name: string;
  description: string | null;
  status: SubscriptionPlan['status'];
  isDefaultForNewUsers: boolean;
  defaultDurationDays: number | null;
};

type SubscriptionPlanModalProps = {
  mode: 'create' | 'edit';
  plan?: SubscriptionPlan;
  onCancel: () => void;
  onSubmit: (payload: SubscriptionPlanFormValues) => void;
};

export function SubscriptionPlansManagement() {
  const { authedRequest } = useAuth();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [deleteModalPlan, setDeleteModalPlan] = useState<SubscriptionPlan | null>(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalog, setCatalog] = useState<StripeCatalogResponse | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);

  useErrorNotification(status, { header: 'Subscription plan error' });

  const loadPlans = useCallback(async () => {
    setLoading(true);
    setStatus(null);
    try {
      const data = await authedRequest<{ items: SubscriptionPlan[] }>('/api/admin/subscription-types');
      setPlans(data.items);
    } catch (err: any) {
      if (err?.status === 404) {
        setPlans([]);
        setStatus(null);
        return;
      }
      setStatus({ type: 'err', text: err?.message || 'Failed to load subscription plans.' });
    } finally {
      setLoading(false);
    }
  }, [authedRequest]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const handleCreate = async (payload: SubscriptionPlanFormValues) => {
    setStatus({ type: 'info', text: 'Creating subscription plan…' });
    try {
      await authedRequest(`/api/admin/subscription-types`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      setStatus({ type: 'ok', text: 'Subscription plan created.' });
      setCreateOpen(false);
      loadPlans();
    } catch (err: any) {
      setStatus({ type: 'err', text: err?.message || 'Failed to create subscription plan.' });
    }
  };

  const handleUpdate = async (plan: SubscriptionPlan, payload: SubscriptionPlanFormValues) => {
    setStatus({ type: 'info', text: 'Updating subscription plan…' });
    try {
      await authedRequest(`/api/admin/subscription-types/${plan.id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      setStatus({ type: 'ok', text: 'Subscription plan updated.' });
      setEditingPlan(null);
      loadPlans();
    } catch (err: any) {
      setStatus({ type: 'err', text: err?.message || 'Failed to update subscription plan.' });
    }
  };

  const openDeleteModal = (plan: SubscriptionPlan) => {
    if (plan.isDefaultForNewUsers) {
      setStatus({ type: 'err', text: 'Cannot delete the default subscription plan.' });
      return;
    }
    setDeleteModalPlan(plan);
  };

  const closeDeleteModal = () => {
    if (deleteInProgress) {
      return;
    }
    setDeleteModalPlan(null);
  };

  const confirmDelete = async () => {
    if (!deleteModalPlan) {
      return;
    }
    setDeleteInProgress(true);
    setStatus({ type: 'info', text: 'Deleting subscription plan…' });
    try {
      await authedRequest(`/api/admin/subscription-types/${deleteModalPlan.id}`, { method: 'DELETE' });
      setStatus({ type: 'ok', text: 'Subscription plan deleted.' });
      setDeleteModalPlan(null);
      await loadPlans();
    } catch (err: any) {
      setStatus({ type: 'err', text: err?.message || 'Failed to delete subscription plan.' });
    } finally {
      setDeleteInProgress(false);
    }
  };

  const handleSyncFromStripe = async () => {
    setSyncing(true);
    setStatus({ type: 'info', text: 'Syncing plans from Stripe…' });
    try {
      const data = await authedRequest<{ updated: number; syncedAt: string }>(
        '/api/admin/billing/stripe/sync',
        { method: 'POST' }
      );
      const updatedMessage = data.updated > 0 ? `Synced ${data.updated} plan(s) from Stripe.` : 'Plans already match Stripe.';
      setStatus({ type: 'ok', text: updatedMessage });
      loadPlans();
    } catch (err: any) {
      setStatus({ type: 'err', text: err?.message || 'Failed to sync plans from Stripe.' });
    } finally {
      setSyncing(false);
    }
  };

  const handleViewCatalog = async () => {
    setCatalogLoading(true);
    try {
      const data = await authedRequest<StripeCatalogResponse>('/api/admin/billing/stripe/catalog');
      setCatalog(data);
      setCatalogOpen(true);
    } catch (err: any) {
      setStatus({ type: 'err', text: err?.message || 'Failed to load Stripe catalog.' });
    } finally {
      setCatalogLoading(false);
    }
  };

  return (
    <div className="card card-outline card-secondary">
      <div className="card-header border-0 d-flex justify-content-between align-items-center gap-2">
        <div>
          <h3 className="card-title mb-0">Subscription plans</h3>
          <p className="text-muted mb-0">Manage available plans and defaults.</p>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleSyncFromStripe}
            disabled={syncing || loading}
          >
            <i className="fas fa-sync-alt me-1" aria-hidden="true" />
            {syncing ? 'Syncing…' : 'Sync from Stripe'}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleViewCatalog}
            disabled={catalogLoading}
          >
            <i className="fas fa-eye me-1" aria-hidden="true" />
            {catalogLoading ? 'Loading…' : 'View Stripe catalog'}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setCreateOpen(true)}>
            <i className="fas fa-plus me-1" aria-hidden="true" /> New plan
          </button>
        </div>
      </div>
      <div className="card-body">
        {status && status.type !== 'err' && (
          <div className={`alert alert-${status.type === 'ok' ? 'success' : 'info'} alert-dismissible fade show`} role="status">
            {status.text}
            <button type="button" className="btn-close" onClick={() => setStatus(null)} aria-label="Dismiss" />
          </div>
        )}
        <div className="table-responsive">
          <table className="table table-striped table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>Plan</th>
                <th>Description</th>
                <th>Status</th>
                <th>Default</th>
                <th>Duration (days)</th>
                <th>Stripe</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => (
                <tr key={plan.id}>
                  <td>
                    <div className="fw-semibold">{plan.name}</div>
                    <div className="text-muted text-uppercase small">{plan.code}</div>
                  </td>
                  <td>{plan.description || '—'}</td>
                  <td>
                    <span className={`badge ${plan.status === 'active' ? 'bg-success' : 'bg-danger'}`}>{plan.status}</span>
                  </td>
                  <td>{plan.isDefaultForNewUsers ? <span className="badge bg-info">Default</span> : '—'}</td>
                  <td>{plan.defaultDurationDays ?? '∞'}</td>
                  <td>
                    {plan.stripeProductId || plan.stripePriceId ? (
                      <div>
                        <span className="badge bg-primary mb-1">Stripe managed</span>
                        <div className="small text-muted">Product: {plan.stripeProductId ?? '—'}</div>
                        <div className="small text-muted">Price: {plan.stripePriceId ?? '—'}</div>
                      </div>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="text-center">
                    <div className="dropdown d-inline-block">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary dropdown-toggle"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        Actions
                      </button>
                      <ul className="dropdown-menu dropdown-menu-end">
                        <li>
                          <button type="button" className="dropdown-item" onClick={() => setEditingPlan(plan)}>
                            Edit
                          </button>
                        </li>
                        <li>
                          <button
                            type="button"
                            className="dropdown-item text-danger"
                            onClick={() => openDeleteModal(plan)}
                            disabled={plan.isDefaultForNewUsers}
                          >
                            Delete
                          </button>
                        </li>
                      </ul>
                    </div>
                  </td>
                </tr>
              ))}
              {plans.length === 0 && !loading ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-4">
                    No subscription plans found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        {loading && <div className="text-muted small">Loading plans…</div>}
      </div>

      {createOpen && (
        <SubscriptionPlanModal
          mode="create"
          onCancel={() => setCreateOpen(false)}
          onSubmit={handleCreate}
        />
      )}

      {editingPlan && (
        <SubscriptionPlanModal
          mode="edit"
          plan={editingPlan}
          onCancel={() => setEditingPlan(null)}
          onSubmit={(payload) => handleUpdate(editingPlan, payload)}
        />
      )}

      {deleteModalPlan && (
        <Modal
          title={`Delete ${deleteModalPlan.name}`}
          onClose={closeDeleteModal}
          footer={
            <>
              <button type="button" className="btn btn-outline-secondary" onClick={closeDeleteModal} disabled={deleteInProgress}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={deleteInProgress}>
                Delete plan
              </button>
            </>
          }
        >
          <div className="mb-3">
            <p className="mb-1">This will permanently remove the plan and cannot be undone.</p>
            <ul className="mb-0 ps-3">
              <li>
                <span className="fw-semibold">Name:</span> {deleteModalPlan.name}
              </li>
              <li>
                <span className="fw-semibold text-uppercase">Code:</span> {deleteModalPlan.code}
              </li>
            </ul>
          </div>
          <div className="alert alert-warning" role="alert">
            Ensure no automation expects this code before continuing.
          </div>
          {deleteInProgress ? (
            <div className="alert alert-info d-flex align-items-center gap-2 mb-0" role="status">
              <span className="spinner-border spinner-border-sm" aria-hidden="true" />
              <span>Deleting plan…</span>
            </div>
          ) : null}
        </Modal>
      )}

      {catalogOpen && catalog && (
        <StripeCatalogModal
          catalog={catalog}
          loading={catalogLoading}
          onReload={handleViewCatalog}
          onClose={() => setCatalogOpen(false)}
        />
      )}
    </div>
  );
}

function SubscriptionPlanModal({ mode, plan, onCancel, onSubmit }: SubscriptionPlanModalProps) {
  const [code, setCode] = useState(plan?.code ?? '');
  const [name, setName] = useState(plan?.name ?? '');
  const [description, setDescription] = useState(plan?.description ?? '');
  const [status, setStatus] = useState<SubscriptionPlan['status']>(plan?.status ?? 'active');
  const [isDefault, setIsDefault] = useState<boolean>(plan?.isDefaultForNewUsers ?? false);
  const [duration, setDuration] = useState<string>(
    plan?.defaultDurationDays != null ? String(plan.defaultDurationDays) : ''
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = mode === 'edit';

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    if (!code.trim()) {
      setError('Code is required.');
      return;
    }
    const durationNumber = duration.trim().length > 0 ? Number(duration) : null;
    if (durationNumber != null && (Number.isNaN(durationNumber) || durationNumber < 0)) {
      setError('Duration must be a positive number.');
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      await onSubmit({
        code: code.trim(),
        name: name.trim(),
        description: description.trim().length > 0 ? description.trim() : null,
        status,
        isDefaultForNewUsers: isDefault,
        defaultDurationDays: durationNumber,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const title = isEdit ? `Edit ${plan?.name}` : 'Create subscription plan';

  return (
    <Modal
      title={title}
      onClose={onCancel}
      size="lg"
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create plan'}
          </button>
        </>
      }
    >
      {isEdit && (plan?.stripeProductId || plan?.stripePriceId) && (
        <div className="alert alert-light border mb-3">
          <div className="fw-semibold">Stripe linkage</div>
          <p className="small mb-1">Product ID: {plan?.stripeProductId ?? '—'}</p>
          <p className="small mb-0">Price ID: {plan?.stripePriceId ?? '—'}</p>
        </div>
      )}
      <form
        className="row g-3"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        <div className="col-12 col-md-6">
          <label className="form-label" htmlFor="planCode">
            Plan code
          </label>
          <input
            id="planCode"
            type="text"
            className="form-control text-uppercase"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            required
          />
          <div className="form-text">
            {isEdit
              ? 'Changing the code updates the identifier that future assignments reference.'
              : 'Use a unique identifier (e.g. PREMIUM2024).'}
          </div>
        </div>
        <div className="col-12 col-md-6">
          <label className="form-label" htmlFor="planName">
            Name
          </label>
          <input
            id="planName"
            type="text"
            className="form-control"
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
          />
        </div>
        <div className="col-12">
          <label className="form-label" htmlFor="planDescription">
            Description
          </label>
          <textarea
            id="planDescription"
            className="form-control"
            rows={3}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>
        <div className="col-12 col-md-6">
          <label className="form-label" htmlFor="planStatus">
            Status
          </label>
          <select
            id="planStatus"
            className="form-select"
            value={status}
            onChange={(event) => setStatus(event.target.value as SubscriptionPlan['status'])}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div className="col-12 col-md-6">
          <label className="form-label" htmlFor="planDuration">
            Default duration (days)
          </label>
          <input
            id="planDuration"
            type="number"
            min={0}
            className="form-control"
            value={duration}
            onChange={(event) => setDuration(event.target.value)}
            placeholder="Leave blank for unlimited"
          />
        </div>
        <div className="col-12">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="planDefault"
              checked={isDefault}
              onChange={(event) => setIsDefault(event.target.checked)}
            />
            <label className="form-check-label" htmlFor="planDefault">
              Default for new users
            </label>
          </div>
        </div>
        {error && (
          <div className="col-12">
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
}

type StripeCatalogModalProps = {
  catalog: StripeCatalogResponse;
  loading: boolean;
  onReload: () => void;
  onClose: () => void;
};

function StripeCatalogModal({ catalog, loading, onReload, onClose }: StripeCatalogModalProps) {
  const formatAmount = (value: number | null, currency: string) => {
    if (value == null) {
      return '—';
    }
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency.toUpperCase(),
      }).format(value / 100);
    } catch {
      return `${(value / 100).toFixed(2)} ${currency.toUpperCase()}`;
    }
  };

  const intervalLabel = (interval: string | null, count: number | null) => {
    if (!interval) return '—';
    if (!count || count === 1) return interval;
    return `${count} ${interval}`;
  };

  return (
    <Modal
      title="Stripe catalog snapshot"
      onClose={onClose}
      size="xl"
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Close
          </button>
          <button type="button" className="btn btn-primary" onClick={onReload} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </>
      }
    >
      <div className="mb-4">
        <h5 className="fw-semibold">Prices</h5>
        <div className="table-responsive">
          <table className="table table-sm table-striped">
            <thead>
              <tr>
                <th>Lookup</th>
                <th>Amount</th>
                <th>Interval</th>
                <th>Active</th>
                <th>Product</th>
              </tr>
            </thead>
            <tbody>
              {catalog.prices.map((price) => (
                <tr key={price.id}>
                  <td>
                    <div className="fw-semibold">{price.lookupKey ?? '—'}</div>
                    <div className="small text-muted">{price.id}</div>
                  </td>
                  <td>{formatAmount(price.unitAmount, price.currency)}</td>
                  <td>{intervalLabel(price.interval, price.intervalCount)}</td>
                  <td>
                    <span className={`badge ${price.active ? 'bg-success' : 'bg-secondary'}`}>{price.active ? 'Active' : 'Inactive'}</span>
                  </td>
                  <td>
                    <div className="fw-semibold">{price.productName ?? '—'}</div>
                    <div className="small text-muted">{price.productId ?? '—'}</div>
                  </td>
                </tr>
              ))}
              {catalog.prices.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-muted text-center py-3">
                    No prices found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mb-4">
        <h5 className="fw-semibold">Promotion codes</h5>
        <div className="table-responsive">
          <table className="table table-sm table-striped">
            <thead>
              <tr>
                <th>Code</th>
                <th>Status</th>
                <th>Coupon</th>
                <th>Usage</th>
              </tr>
            </thead>
            <tbody>
              {catalog.promotionCodes.map((promo) => (
                <tr key={promo.id}>
                  <td>
                    <div className="fw-semibold">{promo.code}</div>
                    <div className="small text-muted">{promo.id}</div>
                  </td>
                  <td>
                    <span className={`badge ${promo.active ? 'bg-success' : 'bg-secondary'}`}>{promo.active ? 'Active' : 'Inactive'}</span>
                  </td>
                  <td>
                    {promo.coupon ? (
                      <div>
                        <div className="small">Duration: {promo.coupon.duration ?? '—'}</div>
                        <div className="small">Amount off: {promo.coupon.amountOff ?? '—'}</div>
                        <div className="small">Percent off: {promo.coupon.percentOff ?? '—'}</div>
                      </div>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>
                    <div className="small">Max: {promo.maxRedemptions ?? '—'}</div>
                    <div className="small">Redeemed: {promo.timesRedeemed ?? 0}</div>
                  </td>
                </tr>
              ))}
              {catalog.promotionCodes.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-muted text-center py-3">
                    No promotion codes found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h5 className="fw-semibold">Products</h5>
        <div className="table-responsive">
          <table className="table table-sm table-striped">
            <thead>
              <tr>
                <th>Product</th>
                <th>Status</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {catalog.products.map((product) => (
                <tr key={product.id}>
                  <td>
                    <div className="fw-semibold">{product.name}</div>
                    <div className="small text-muted">{product.id}</div>
                  </td>
                  <td>
                    <span className={`badge ${product.active ? 'bg-success' : 'bg-secondary'}`}>{product.active ? 'Active' : 'Inactive'}</span>
                  </td>
                  <td>{product.description || '—'}</td>
                </tr>
              ))}
              {catalog.products.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-muted text-center py-3">
                    No products found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
}
